import { Alert, Button, Space, Typography } from 'antd';
import { useMemo } from 'react';
import { dropIssuesResolvedByExtraction } from '@/features/inventorization/domain/inventorization-effective-validation';
import type { InventorizationRegisterReading } from '@/features/inventorization/domain/inventorization-reading';
import { INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS } from '@/features/inventorization/domain/review/inventorize-review-field-ids';
import type { InventorizeReviewField } from '@/features/inventorization/domain/review/inventorize-review-fields';
import {
  createNextManualRegister,
  getRegisterPhotos,
} from '@/features/inventorization/inventorization-register-review-actions';
import { reviewText } from '@/features/inventorization/review-i18n';
import { InventorizeParsedReview } from '@/routers/inventorization/components/InventorizeParsedReview';
import { createCapturedPhotoAssetFromFile } from '@/shared/camera/browser-photo-capture';
import type { CapturedPhotoAsset } from '@/shared/camera/photo-capture-adapter';
import {
  CAPTURE_SESSION_STATUSES,
  type CaptureSession,
  type LocalPhotoRef,
  PHOTO_CONTEXTS,
  type ReadinessSummary,
  type ValidationIssue,
} from '@/shared/capture/capture-session';
import type { PhotoSlotDefinition } from '@/shared/capture/inventorization-photo-slots';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

interface InventorizationReviewStageProps {
  activeFieldId: string | null;
  extractionError: string | null;
  fields: InventorizeReviewField[];
  isExtractionPolling: boolean;
  isQueueing: boolean;
  issues: ValidationIssue[];
  onCapturePhoto: (slot: PhotoSlotDefinition, asset: CapturedPhotoAsset) => Promise<LocalPhotoRef | undefined>;
  onApproveField?: (field: InventorizeReviewField) => void;
  onCloseField: () => void;
  onDeletePhoto: (photo: LocalPhotoRef) => Promise<void>;
  onOpenField: (field: InventorizeReviewField) => void;
  onQueue: () => Promise<CaptureSession | undefined>;
  onQueuedToToday: () => void;
  onRefresh: () => Promise<void>;
  onRemoveRegisterReading: (registerId: string) => Promise<void>;
  onSaveRegisterReading: (registerId: string, patch: Partial<InventorizationRegisterReading>) => Promise<void>;
  queueError: string | null;
  readiness: ReadinessSummary;
  session: CaptureSession;
}

export function InventorizationReviewStage({
  activeFieldId,
  extractionError,
  fields,
  isExtractionPolling,
  isQueueing,
  issues,
  onCapturePhoto,
  onApproveField,
  onCloseField,
  onDeletePhoto,
  onOpenField,
  onQueue,
  onQueuedToToday,
  onRefresh,
  onRemoveRegisterReading,
  onSaveRegisterReading,
  queueError,
  readiness,
  session,
}: InventorizationReviewStageProps) {
  const reviewIssues = useMemo(() => dropIssuesResolvedByExtraction(session).validationErrors, [session]);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {queueError ? <Alert closable message={queueError} showIcon type="error" /> : null}
      {issues.length > 0 ? (
        <ReviewIssuesAlert
          issues={issues}
          message={reviewText('queue.missingData', 'Queued with missing data')}
          type="info"
        />
      ) : null}
      {session.status === CAPTURE_SESSION_STATUSES.needsReview && reviewIssues.length > 0 ? (
        <ReviewIssuesAlert
          fields={fields}
          issues={reviewIssues}
          onOpenField={onOpenField}
          message={translateInventoryText('app.inventory.sync.needsReview', 'Needs review')}
          type="error"
        />
      ) : null}

      {activeFieldId ? (
        <UnavailableFieldPanel onCloseField={onCloseField} />
      ) : (
        <InventorizeParsedReview
          extractionError={extractionError}
          isExtractionPolling={isExtractionPolling}
          session={session}
          fields={fields}
          onApproveField={onApproveField}
          onAddRegister={async () => {
            const register = createNextManualRegister(session);

            await onSaveRegisterReading(register.id, register);
          }}
          onCaptureRegisterPhoto={async (registerId, file) => {
            const asset = await createCapturedPhotoAssetFromFile(file);

            await onCapturePhoto(
              {
                context: PHOTO_CONTEXTS.register,
                id: `register-${registerId}`,
                isRequired: true,
                label: reviewText('register.photoLabel', 'Register photo'),
                registerId,
              },
              asset,
            );
            await onRefresh();
          }}
          onRemoveRegister={async registerId => {
            for (const photo of getRegisterPhotos(session, registerId)) {
              await onDeletePhoto(photo);
            }

            await onRemoveRegisterReading(registerId);
            await onRefresh();
          }}
          onOpenField={onOpenField}
          isQueueing={isQueueing}
          onQueueOffline={async () => {
            const queuedSession = await onQueue();

            if (queuedSession) {
              onQueuedToToday();
            }
          }}
          readiness={readiness}
        />
      )}
    </Space>
  );
}

function ReviewIssuesAlert({
  fields,
  issues,
  message,
  onOpenField,
  type,
}: {
  fields?: InventorizeReviewField[];
  issues: ValidationIssue[];
  message: string;
  onOpenField?: (field: InventorizeReviewField) => void;
  type: 'error' | 'info';
}) {
  return (
    <Alert
      closable
      description={
        <ul style={{ margin: 0, paddingInlineStart: 18 }}>
          {issues.map(issue => {
            const field = fields?.find(candidate => candidate.id === issue.fieldId);

            return (
              <li key={issue.code}>
                {issue.message}
                {field && onOpenField ? (
                  <Button onClick={() => onOpenField(field)} type="link">
                    {field.id === INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.apiKey
                      ? reviewText('actions.checkQrApiKey', 'Check QR/API key')
                      : field.label}
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      }
      message={message}
      showIcon
      type={type}
    />
  );
}

function UnavailableFieldPanel({ onCloseField }: { onCloseField: () => void }) {
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Text type="secondary">
          {reviewText('unavailableField.eyebrow', 'Field unavailable')}
        </Typography.Text>
        <Typography.Title level={3} style={{ marginTop: 0 }}>
          {reviewText('unavailableField.title', 'Cannot open field')}
        </Typography.Title>
        <Typography.Paragraph type="secondary">
          {reviewText('unavailableField.description', 'The requested field is not available in this draft.')}
        </Typography.Paragraph>
      </div>
      <Button onClick={onCloseField}>{reviewText('actions.backToReview', 'Back to review')}</Button>
    </Space>
  );
}
